import { Compass } from 'lucide-react';
import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { ListDetail } from '@/ui/ListDetail';
import { StateCard } from '@/ui/StateCard';

import { ReleaseCard } from './ReleaseCard';
import { ReleaseSearch, releaseRows } from './ReleaseList';
import { matching, releaseFor, type Release } from './releaseIndex';

/** The release a person lands on: the one asked for (exact match only), else the running one, else the newest. */
function chosen(releases: readonly Release[], asked: string | undefined, running: string): Release | undefined {
  return asked ? releaseFor(asked, releases) : (releaseFor(running, releases) ?? releases[0]);
}

/** The release list and the selected release's card; the selection is the route's `:version` (`/whats-new/<version>`). */
export function ReleaseBrowser({
  releases,
  runningVersion,
  deployedAt,
  loadBody,
}: {
  releases: readonly Release[];
  runningVersion: string;
  deployedAt: string | null | undefined;
  loadBody: (version: string) => Promise<string>;
}) {
  const { version: asked } = useParams();
  const navigate = useNavigate();
  const [text, setText] = useState('');
  const release = chosen(releases, asked, runningVersion);
  const shown = matching(releases, text);
  const position = release ? releases.indexOf(release) : -1;
  // Versions are looked up in the index and encoded; they never become a file path.
  const open = (version: string) => navigate(`/whats-new/${encodeURIComponent(version)}`, { replace: true });

  return (
    <ListDetail
      label="Releases"
      header={<ReleaseSearch text={text} onText={setText} shown={shown.length} total={releases.length} />}
      list={releaseRows({ releases: shown, selected: release?.version, running: runningVersion, onSelect: open })}
      detail={
        release ? (
          <ReleaseCard
            release={release}
            isRunning={release.version === runningVersion}
            deployedAt={deployedAt}
            newer={releases[position - 1]}
            older={releases[position + 1]}
            onSelect={open}
            loadBody={loadBody}
          />
        ) : (
          <StateCard icon={<Compass aria-hidden="true" />} title={`No notes for version ${asked ?? ''}`.trim()}>
            <p>Choose a release in the list.</p>
          </StateCard>
        )
      }
    />
  );
}
